import type { ImportVoucherRow } from '@/marketing/models/voucher';

// Upfluence carry-over sheet → import rows. A row without a coupon code has one issued for it by the
// backend; a row with one is recorded as-is, because that code is already in the creator's hands.

type VoucherCsvField = 'handle' | 'platform' | 'commissionPercent' | 'couponCode' | 'discountPercent';

const HEADER_ALIASES: Record<string, VoucherCsvField> = {
  handle: 'handle',
  username: 'handle',
  'influencer handle': 'handle',
  'creator handle': 'handle',
  'social media handle': 'handle',
  platform: 'platform',
  'social platform': 'platform',
  commission: 'commissionPercent',
  'commission percent': 'commissionPercent',
  'commission rate': 'commissionPercent',
  code: 'couponCode',
  'coupon code': 'couponCode',
  'voucher code': 'couponCode',
  'existing code': 'couponCode',
  discount: 'discountPercent',
  'discount percent': 'discountPercent',
  'discount rate': 'discountPercent',
};

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;
const MIN_DISCOUNT_PERCENT = 1;

export interface VoucherCsvParseResult {
  rows: ImportVoucherRow[];
  errors: string[];
  headers: string[];
}

function normalizeHeader(header: string): VoucherCsvField | null {
  const cleaned = header
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return HEADER_ALIASES[cleaned] ?? null;
}

function parseLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index++) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index++;
        } else {
          inQuotes = false;
        }
      } else {
        current += character;
      }
    } else if (character === '"') {
      inQuotes = true;
    } else if (character === ',' || character === '\t') {
      fields.push(current.trim());
      current = '';
    } else {
      current += character;
    }
  }

  fields.push(current.trim());
  return fields;
}

function parsePercent(raw: string): number | null {
  const cleaned = raw.replace('%', '').trim();
  // Number('') is 0, which would silently turn a blank cell into a 0% rate.
  if (cleaned.length === 0) {
    return null;
  }

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < MIN_PERCENT || parsed > MAX_PERCENT) {
    return null;
  }
  return parsed;
}

function buildRow(cells: Record<VoucherCsvField, string>, defaultPlatform: string): ImportVoucherRow | string {
  const handle = cells.handle.replace(/^@/, '');
  if (handle.length === 0) {
    return 'missing handle';
  }

  const commissionPercent = parsePercent(cells.commissionPercent);
  if (commissionPercent === null) {
    return cells.commissionPercent.length === 0
      ? 'missing commission percent'
      : `invalid commission percent "${cells.commissionPercent}"`;
  }

  const couponCode = cells.couponCode;
  const rawDiscount = cells.discountPercent;
  const discountPercent = rawDiscount.length > 0 ? parsePercent(rawDiscount) : null;
  if (rawDiscount.length > 0 && (discountPercent === null || discountPercent < MIN_DISCOUNT_PERCENT)) {
    return `invalid discount percent "${rawDiscount}"`;
  }

  return {
    handle,
    platform: (cells.platform || defaultPlatform).toLowerCase(),
    commissionPercent,
    ...(couponCode.length > 0 ? { couponCode } : {}),
    ...(discountPercent !== null ? { discountPercent } : {}),
  };
}

export function parseVoucherCsv(content: string, defaultPlatform = 'tiktok'): VoucherCsvParseResult {
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) {
    return { rows: [], errors: ['CSV file is empty or has no data rows'], headers: [] };
  }

  const rawHeaders = parseLine(lines[0] ?? '');
  const mappedHeaders = rawHeaders.map(normalizeHeader);
  if (!mappedHeaders.includes('handle')) {
    return { rows: [], errors: ['CSV has no handle column'], headers: rawHeaders };
  }
  if (!mappedHeaders.includes('commissionPercent')) {
    return { rows: [], errors: ['CSV has no commission column'], headers: rawHeaders };
  }

  const rows: ImportVoucherRow[] = [];
  const errors: string[] = [];

  for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
    const fields = parseLine(lines[lineIndex] ?? '');
    if (fields.every((field) => field.length === 0)) {
      continue;
    }

    const cells: Record<VoucherCsvField, string> = {
      handle: '',
      platform: '',
      commissionPercent: '',
      couponCode: '',
      discountPercent: '',
    };
    mappedHeaders.forEach((field, columnIndex) => {
      if (field) {
        cells[field] = fields[columnIndex] ?? '';
      }
    });

    const row = buildRow(cells, defaultPlatform);
    if (typeof row === 'string') {
      errors.push(`Row ${lineIndex + 1}: ${row}`);
      continue;
    }
    rows.push(row);
  }

  return { rows, errors, headers: rawHeaders };
}
